import React from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { Grid, Box, Typography } from '@material-ui/core';
import { Form as FinalForm } from 'react-final-form';
import { createMessageOneToManyInboxAndConversation } from 'graphQL/mutation';
import { sendMessageWithAttributes } from 'modules/shell/external/utils';
import { isNumber, validateName, validateEmailV2 } from 'utils';

import Drawer from '../../../Drawer';
import Form from '../../../Form';
import Button from '../../../Button';
import Preloader from '../../../Preloader';

const PHONE_TYPES = [
  {
    name: 'Mobile',
    value: 'MOBILE',
  },
  {
    name: 'Other',
    value: 'OTHER',
  },
];

export const validateForm = (values = {}) => {
  const requiredFields = ['firstName', 'lastName', 'phone', 'phoneType', 'email'];

  // validate required fields
  const errors = requiredFields.reduce((res, fieldName) => {
    if (!values[fieldName]) {
      res[fieldName] = 'Required field';
    }

    return res;
  }, {});

  if (!errors.firstName && !validateName(values.firstName)) {
    errors.firstName = 'Should be two letters or more, without digits';
  }

  if (!errors.lastName && !validateName(values.lastName)) {
    errors.lastName = 'Should be two letters or more, without digits';
  }

  if (!errors.phone && values.phone.length !== 10) {
    errors.phone = 'Should be 10 digits';
  }

  if (!errors.email && !validateEmailV2(values.email)) {
    errors.email = 'Invalid email';
  }

  return errors;
};

const AddProfileForm = ({
  classes,
  history,
  config,
  user,
  createFullClientsProfile,
  initialValues,
  customOnCreateNewProfileHandler,
  onClose,
  bgAccountCreationFlowEnabled,
}) => {
  const [phoneTypesOpened, setPhoneTypesOpened] = React.useState(false);

  const [postMutation] = useMutation(createMessageOneToManyInboxAndConversation);

  const onSubmit = async (values) => {
    const correctedFormValues = {
      ...values,
      email: values.email.toLowerCase(),
      firstName: values.firstName.toUpperCase(),
      lastName: values.lastName.toUpperCase(),
    };
    const onProfileCreate = (customer) => {
      const { cmdUri, onlineProfileId } = customer;

      if (cmdUri) {
        if (onlineProfileId && typeof customOnCreateNewProfileHandler === 'function') {
          customOnCreateNewProfileHandler({ accountId: onlineProfileId, cmdUri });
          onClose();
          return;
        }

        sendMessageWithAttributes({ user, config, customer, templateType: 'clientCreation', postMutation });
        history.push(`/clients/universal-profile/${cmdUri}`);
      }
    };
    const res = await createFullClientsProfile(correctedFormValues, bgAccountCreationFlowEnabled);

    // Redirect user to appropriate route in case of posted profile is already existing
    if (res.status === 'exists') {
      const { customers } = res.data;

      history.push({
        pathname: '/clients/accounts-exist',
        state: { customers },
      });

      return;
    }

    // Complete profile creation flow
    if (res.status === 'created') {
      onProfileCreate(res.data);
    }
  };

  return (
    <FinalForm onSubmit={onSubmit} validate={validateForm} initialValues={initialValues}>
      {({ form, submitting, invalid, handleSubmit }) => (
        <>
          {submitting && <Preloader />}
          <Form onSubmit={handleSubmit} autoComplete="off" className={classes.container}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Form.Input name="firstName" label="First Name" required />
              </Grid>
              <Grid item xs={6}>
                <Form.Input name="lastName" label="Last Name" required />
              </Grid>
              <Grid item xs={6}>
                <Form.Input name="phone" label="Phone Number" parse={isNumber} required />
              </Grid>
              <Grid item xs={6}>
                <Form.Select
                  name="phoneType"
                  label="Type"
                  aria-label="select"
                  onClick={() => setPhoneTypesOpened(true)}
                  required
                  readOnly
                >
                  {PHONE_TYPES}
                </Form.Select>
              </Grid>
              <Grid item xs={12}>
                <Form.Input name="email" label="Email Address" required />
              </Grid>
            </Grid>
            <div className={classes.growBlock} />
            <Grid container>
              <Button
                onClick={form.submit}
                label="Create Profile"
                disabled={submitting || invalid}
                data-testid="btn-shipping-form-update"
              />
            </Grid>
          </Form>
          <Drawer title="Phone Type" open={phoneTypesOpened} onClose={() => setPhoneTypesOpened(false)}>
            <Box className={classes.phoneTypesContainer}>
              {PHONE_TYPES.map(({ name, value }) => (
                <Typography
                  variant="body1"
                  key={name}
                  className={classes.phoneTypeItem}
                  onClick={() => {
                    form.change('phoneType', value);
                    setPhoneTypesOpened(false);
                  }}
                >
                  {name}
                </Typography>
              ))}
            </Box>
          </Drawer>
        </>
      )}
    </FinalForm>
  );
};

AddProfileForm.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  config: PropTypes.objectOf(PropTypes.string),
  user: PropTypes.objectOf(PropTypes.string),
  createFullClientsProfile: PropTypes.func,
  customOnCreateNewProfileHandler: PropTypes.func,
  onClose: PropTypes.func,
  bgAccountCreationFlowEnabled: PropTypes.bool,
};

AddProfileForm.defaultProps = {
  classes: {},
};

export default AddProfileForm;
